import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, differenceInHours, differenceInDays } from 'date-fns';

export interface DataPoint {
  timestamp: string;
  value: number;
  pondId?: string;
  pondName?: string;
}

export interface TrendSeries {
  pondId: string;
  pondName: string;
  data: DataPoint[];
  color: string;
}

// Recharts merged data point type
interface MergedDataPoint {
  timestamp: number;
  [pondId: string]: number | undefined;
}

// Recharts tooltip payload item
interface TooltipPayloadItem {
  dataKey: string;
  value: number;
  color: string;
  name: string;
}

interface TrendChartProps {
  series: TrendSeries[];
  metricLabel: string;
  metricUnit?: string;
  className?: string;
  height?: number;
  onZoomSelect?: (range: { startDate: Date; endDate: Date }) => void;
}

// Downsample data to max 100 points for performance
const downsampleData = (data: DataPoint[], maxPoints: number = 100): DataPoint[] => {
  if (data.length <= maxPoints) return data;

  const step = Math.ceil(data.length / maxPoints);
  return data.filter((_, index) => index % step === 0);
};

// Generate nice, rounded tick values based on time range
const generateNiceTicks = (timestamps: number[]): number[] => {
  if (timestamps.length === 0) return [];

  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  const startDate = new Date(minTime);
  const endDate = new Date(maxTime);

  const hours = differenceInHours(endDate, startDate);
  const days = differenceInDays(endDate, startDate);

  const ticks: number[] = [];
  let current = new Date(startDate);

  // Round start to nearest nice interval
  if (hours <= 6) {
    // Show every hour on the hour (00:00)
    current.setMinutes(0, 0, 0);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 60 * 60 * 1000); // +1 hour
    }
  } else if (hours <= 12) {
    // Show every 2 hours (00:00, 02:00, 04:00, etc.)
    current.setMinutes(0, 0, 0);
    const currentHour = current.getHours();
    current.setHours(Math.floor(currentHour / 2) * 2);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 2 * 60 * 60 * 1000); // +2 hours
    }
  } else if (hours <= 24) {
    // Show every 3 hours (00:00, 03:00, 06:00, 09:00, etc.)
    current.setMinutes(0, 0, 0);
    const currentHour = current.getHours();
    current.setHours(Math.floor(currentHour / 3) * 3);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 3 * 60 * 60 * 1000); // +3 hours
    }
  } else if (days <= 3) {
    // Show every 6 hours (00:00, 06:00, 12:00, 18:00)
    current.setMinutes(0, 0, 0);
    const currentHour = current.getHours();
    current.setHours(Math.floor(currentHour / 6) * 6);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 6 * 60 * 60 * 1000); // +6 hours
    }
  } else if (days <= 7) {
    // Show every 12 hours (00:00, 12:00)
    current.setMinutes(0, 0, 0);
    const currentHour = current.getHours();
    current.setHours(currentHour < 12 ? 0 : 12);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 12 * 60 * 60 * 1000); // +12 hours
    }
  } else if (days <= 14) {
    // Show daily at midnight
    current.setHours(0, 0, 0, 0);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 24 * 60 * 60 * 1000); // +1 day
    }
  } else if (days <= 30) {
    // Show every 2 days at midnight
    current.setHours(0, 0, 0, 0);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 2 * 24 * 60 * 60 * 1000); // +2 days
    }
  } else {
    // Show every 7 days at midnight
    current.setHours(0, 0, 0, 0);
    // Round to start of week (Monday)
    const day = current.getDay();
    const daysToMonday = day === 0 ? 6 : day - 1;
    current.setDate(current.getDate() - daysToMonday);
    while (current <= endDate) {
      if (current >= startDate) {
        ticks.push(current.getTime());
      }
      current = new Date(current.getTime() + 7 * 24 * 60 * 60 * 1000); // +7 days
    }
  }

  // Always include the end date if it's not already included
  if (ticks.length === 0 || ticks[ticks.length - 1] !== maxTime) {
    ticks.push(maxTime);
  }

  return ticks;
};

// Merge multiple pond series into unified data for Recharts
const mergeSeriesData = (series: TrendSeries[]): MergedDataPoint[] => {
  const timestampMap = new Map<number, MergedDataPoint>();

  series.forEach((s) => {
    const downsampledData = downsampleData(s.data);
    downsampledData.forEach((point) => {
      const timestamp = new Date(point.timestamp).getTime(); // Convert to numeric timestamp
      if (!timestampMap.has(timestamp)) {
        timestampMap.set(timestamp, { timestamp });
      }
      const entry = timestampMap.get(timestamp)!;
      entry[s.pondId] = point.value;
    });
  });

  return Array.from(timestampMap.values()).sort(
    (a, b) => a.timestamp - b.timestamp
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
  metricUnit?: string;
  series: TrendSeries[];
}

const CustomTooltip = ({ active, payload, label, metricUnit, series }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length || !label) return null;

  return (
    <div className="rounded-lg border border-slate-200/50 bg-white p-3 shadow-lg">
      <p className="text-xs font-medium text-gray-600 mb-2">
        {format(new Date(Number(label)), 'MMM d, yyyy HH:mm')}
      </p>
      {payload.map((entry) => {
        const seriesItem = series.find(s => s.pondId === entry.dataKey);
        return (
          <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="font-medium text-gray-900">
              {seriesItem?.pondName || entry.dataKey}:
            </span>
            <span className="text-gray-700">
              {entry.value.toFixed(2)} {metricUnit || ''}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const TrendChart: React.FC<TrendChartProps> = ({
  series,
  metricLabel,
  metricUnit,
  className = '',
  height = 400,
  onZoomSelect,
}) => {
  const chartData = useMemo(() => mergeSeriesData(series), [series]);
  const niceTicks = useMemo(() => {
    const timestamps = chartData.map(d => d.timestamp);
    return generateNiceTicks(timestamps);
  }, [chartData]);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [zoomState, setZoomState] = useState<{
    isDragging: boolean;
    startX: number | null;
    endX: number | null;
    chartLeft: number;
    chartWidth: number;
  }>({
    isDragging: false,
    startX: null,
    endX: null,
    chartLeft: 0,
    chartWidth: 0,
  });

  const hasData = series.some(s => s.data.length > 0);

  const handleLegendClick = (pondId: string) => {
    setHiddenSeries((prev) => {
      const newHidden = new Set(prev);
      if (newHidden.has(pondId)) {
        newHidden.delete(pondId);
      } else {
        newHidden.add(pondId);
      }
      return newHidden;
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onZoomSelect || !e) return;

    const chartElement = e.currentTarget;
    const rect = chartElement.getBoundingClientRect();
    const x = e.clientX - rect.left;

    setZoomState({
      isDragging: true,
      startX: x,
      endX: x,
      chartLeft: rect.left,
      chartWidth: rect.width,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!zoomState.isDragging || !e) return;

    const chartElement = e.currentTarget;
    const rect = chartElement.getBoundingClientRect();
    const x = e.clientX - rect.left;

    setZoomState((prev) => ({
      ...prev,
      endX: x,
    }));
  };

  const handleMouseUp = () => {
    if (!zoomState.isDragging || !onZoomSelect || zoomState.startX === null || zoomState.endX === null) {
      setZoomState({ isDragging: false, startX: null, endX: null, chartLeft: 0, chartWidth: 0 });
      return;
    }

    // Get time domain from chart data
    const timestamps = chartData.map(d => d.timestamp);
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    const timeRange = maxTime - minTime;

    // Convert pixel coordinates to timestamps
    const startRatio = Math.min(zoomState.startX, zoomState.endX) / zoomState.chartWidth;
    const endRatio = Math.max(zoomState.startX, zoomState.endX) / zoomState.chartWidth;

    const startTimestamp = minTime + (startRatio * timeRange);
    const endTimestamp = minTime + (endRatio * timeRange);

    const startDate = new Date(startTimestamp);
    const endDate = new Date(endTimestamp);

    // Enforce minimum 30 second selection
    const durationMs = endTimestamp - startTimestamp;
    if (durationMs >= 30000) {
      onZoomSelect({ startDate, endDate });
    }

    setZoomState({ isDragging: false, startX: null, endX: null, chartLeft: 0, chartWidth: 0 });
  };

  const handleMouseLeave = () => {
    if (zoomState.isDragging) {
      setZoomState({ isDragging: false, startX: null, endX: null, chartLeft: 0, chartWidth: 0 });
    }
  };

  // Calculate overlay position for zoom selection (must be before early returns)
  const overlayProps = useMemo(() => {
    if (!zoomState.isDragging || zoomState.startX === null || zoomState.endX === null) {
      return null;
    }

    const left = Math.min(zoomState.startX, zoomState.endX);
    const width = Math.abs(zoomState.endX - zoomState.startX);

    return { left, width };
  }, [zoomState]);

  if (!hasData) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-slate-200/50 bg-gray-50 ${className}`}
        style={{ height }}
      >
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className="mt-2 text-sm font-medium text-gray-900">No data available</p>
          <p className="text-xs text-gray-500">Select ponds and a date range to view trends</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-slate-200/50 bg-white p-4 shadow-sm ${className}`}>
      <h4 className="text-sm font-semibold text-gray-900 mb-4">
        {metricLabel} {metricUnit && `(${metricUnit})`}
      </h4>
      <div
        className="relative"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: onZoomSelect ? 'crosshair' : 'default' }}
      >
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            ticks={niceTicks}
            tickFormatter={(value) => format(new Date(value), 'MMM d HH:mm')}
            stroke="#64748b"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#64748b"
            style={{ fontSize: '12px' }}
            label={{ value: metricUnit || '', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
          />
          <Tooltip
            content={<CustomTooltip metricUnit={metricUnit} series={series} />}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }}
            formatter={(value) => series.find(s => s.pondId === value)?.pondName || value}
            onClick={(e) => e.dataKey && handleLegendClick(String(e.dataKey))}
            iconType="line"
          />
          {series.map((s) => (
            <Line
              key={s.pondId}
              type="monotone"
              dataKey={s.pondId}
              name={s.pondName}
              stroke={s.color}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
              hide={hiddenSeries.has(s.pondId)}
              strokeOpacity={hiddenSeries.has(s.pondId) ? 0.2 : 1}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

        {/* Zoom selection overlay */}
        {overlayProps && (
          <div
            className="absolute top-0 pointer-events-none"
            style={{
              left: `${overlayProps.left}px`,
              width: `${overlayProps.width}px`,
              height: '100%',
              backgroundColor: 'rgba(16, 185, 129, 0.2)',
              border: '1px solid rgba(16, 185, 129, 0.5)',
            }}
          />
        )}
      </div>

      {chartData.length === 100 && (
        <p className="mt-3 text-xs text-amber-600 flex items-center gap-1">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Data downsampled to 100 points for optimal performance
        </p>
      )}
    </div>
  );
};
